import { useEffect, useRef } from "react";

import AlertFilled from "chameleon/icons/alert-filled";
import CircleCheckFilled from "chameleon/icons/circle-check-filled";
import InfoCheckFilled from "chameleon/icons/info-check-filled";
import { Text } from "chameleon/lib/components";
import { ThemeTypographyKey } from "chameleon/lib/theme/theme";

import * as styles from "./alert.css";

interface AlertProps {
  /**
   * Variant styling changes color
   */
  variant: "Error" | "Success" | "Info";
  /**
   * Title is not necessary but usable
   */
  title?: string | null;
  /**
   * Core description
   */
  description: string;
  /**
   * StyleVariant to set the header style
   */
  styleVariant?: ThemeTypographyKey;
}

/**
 *
 * @example
 * <Alert variant="Error" title="Error Title" description="Error Description" />
 */
export const Alert = ({
  variant = "Error",
  title,
  description,
  styleVariant = "heading4",
}: AlertProps) => {
  const variantIconMap = {
    Error: <AlertFilled color="critical" role="presentation" />,
    Success: <CircleCheckFilled color="positive" role="presentation" />,
    Info: <InfoCheckFilled color="info" role="presentation" />,
  };

  const sectionRef = useRef<HTMLElement>(null);

  const cardStyleMap = {
    Error: styles.errorCard,
    Info: styles.infoCard,
    Success: styles.successCard,
  };

  const colorVariantMap = {
    Error: "critical",
    Info: "info",
    Success: "positive",
  } as const;

  const cardStyleClass = cardStyleMap[variant];
  const titleColorVariant = colorVariantMap[variant];

  useEffect(() => {
    sectionRef.current?.scrollIntoView?.({ behavior: "smooth" });
  }, []);

  return (
    <section
      role="alert"
      className={`${styles.card} ${cardStyleClass}`}
      data-testid="alert"
      ref={sectionRef}
    >
      {variantIconMap[variant]}
      <div className={styles.cardContentWrapper}>
        {title && ( // only show title if it is supplied, otherwise nothing
          <Text
            data-testid="alertTitle"
            styleVariant={styleVariant}
            colorVariant={titleColorVariant}
          >
            {title}
          </Text>
        )}
        <Text styleVariant="body" colorVariant="primaryText">
          {description}
        </Text>
      </div>
    </section>
  );
};
